<template>
  <div class="school-news-management">
    <!-- Action Bar -->
    <div class="action-bar">
      <div class="action-bar-left">
        <button @click="showCreateForm = !showCreateForm" class="btn-primary">
          <i :class="showCreateForm ? 'fas fa-times' : 'fas fa-plus'"></i>
          <span>{{ showCreateForm ? 'Cancelar' : 'Nueva Noticia' }}</span>
        </button>
        <button @click="showImportModal = true" class="btn-secondary">
          <i class="fas fa-file-import"></i>
          <span>Importar</span>
        </button>
        <button @click="showExportModal = true" class="btn-secondary" :disabled="schoolNews.length === 0">
          <i class="fas fa-file-export"></i>
          <span>Exportar</span>
        </button>
      </div>
      <div class="action-bar-right">
        <span class="news-count">
          <i class="fas fa-newspaper"></i>
          {{ filteredSchoolNews.length }} de {{ schoolNews.length }} noticia{{ schoolNews.length !== 1 ? 's' : '' }}
        </span>
      </div>
    </div>

    <!-- Create School News Form (Collapsible) -->
    <div v-if="showCreateForm" class="create-form-section">
      <form @submit.prevent="handleCreateSchoolNews" class="news-form">
        <div class="form-group">
          <label for="school-news-title" class="form-label">Título</label>
          <input
            id="school-news-title"
            v-model="newSchoolNews.title"
            type="text"
            class="form-input"
            placeholder="Título de la noticia"
            :disabled="creatingSchoolNews"
            required
          />
        </div>

        <div class="form-group">
          <label for="school-news-content" class="form-label">Contenido</label>
          <QuillEditor
            ref="schoolNewsCreateEditorRef"
            v-model:content="newSchoolNews.content"
            :options="quillOptions"
            :disabled="creatingSchoolNews"
            content-type="html"
            class="quill-editor"
          />
        </div>

        <div class="form-group">
          <label for="school-news-createdAt" class="form-label">Fecha de publicación (opcional)</label>
          <input
            id="school-news-createdAt"
            v-model="newSchoolNews.createdAt"
            type="datetime-local"
            class="form-input"
            :disabled="creatingSchoolNews"
          />
          <small class="form-help">Si no se especifica, se usará la fecha y hora actuales</small>
        </div>

        <div class="form-actions">
          <button
            type="submit"
            class="create-button"
            :disabled="creatingSchoolNews || !newSchoolNews.title.trim() || !newSchoolNews.content.trim()"
          >
            <span v-if="creatingSchoolNews" class="loading-spinner">
              <i class="fas fa-spinner fa-spin"></i>
            </span>
            <span v-else>
              <i class="fas fa-plus"></i>
              Publicar Noticia
            </span>
          </button>
        </div>
      </form>
    </div>

    <!-- Search and Filter -->
    <div class="search-filter-section">
      <div class="search-input-wrapper">
        <i class="fas fa-search search-icon"></i>
        <input
          v-model="searchQuery"
          type="text"
          class="search-input"
          placeholder="Buscar por título o contenido..."
        />
        <button v-if="searchQuery" @click="searchQuery = ''" class="clear-search">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="results-per-page">
        <label>Mostrar:</label>
        <select v-model="itemsPerPage" class="page-size-select">
          <option :value="10">10</option>
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="schoolNewsLoading" class="loading-state">
      <div class="loading-spinner">
        <i class="fas fa-spinner fa-spin"></i>
      </div>
      <p>Cargando noticias...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredSchoolNews.length === 0" class="empty-state">
      <div class="empty-icon">
        <i class="fas fa-inbox"></i>
      </div>
      <h3>{{ searchQuery ? 'No se encontraron noticias' : 'No hay noticias' }}</h3>
      <p>{{ searchQuery ? 'Intenta con otros términos de búsqueda.' : 'Crea la primera noticia de la escuela usando el formulario de arriba.' }}</p>
      <button v-if="searchQuery" @click="searchQuery = ''" class="clear-search-button">
        Limpiar búsqueda
      </button>
    </div>

    <!-- School News Table -->
    <div v-else class="news-table-wrapper">
      <table class="news-table">
        <thead>
          <tr>
            <th class="col-title">Título</th>
            <th class="col-date">Fecha</th>
            <th class="col-author">Autor</th>
            <th class="col-actions">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in paginatedSchoolNews" :key="item.id" class="news-row">
            <td class="col-title">
              <div class="news-title-cell">
                <span class="news-title">{{ item.title }}</span>
                <span class="news-preview">{{ truncateContent(stripHtml(item.content), 80) }}</span>
              </div>
            </td>
            <td class="col-date">
              <time :datetime="item.createdAt.toISOString()">
                {{ formatDate(item.createdAt) }}
              </time>
            </td>
            <td class="col-author">
              <div class="author-cell">
                <i class="fas fa-user"></i>
                <span>{{ item.author }}</span>
              </div>
            </td>
            <td class="col-actions">
              <div class="action-buttons">
                <button
                  @click="openEditSchoolNewsModal(item)"
                  class="edit-button"
                  :title="'Editar: ' + item.title"
                >
                  <i class="fas fa-edit"></i>
                </button>
                <button
                  @click="handleDeleteSchoolNews(item)"
                  class="delete-button"
                  :disabled="deletingSchoolNews === item.id"
                  :title="'Eliminar: ' + item.title"
                >
                  <i v-if="deletingSchoolNews === item.id" class="fas fa-spinner fa-spin"></i>
                  <i v-else class="fas fa-trash-alt"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="pagination">
        <button
          @click="currentPage = 1"
          :disabled="currentPage === 1"
          class="page-button"
          title="Primera página"
        >
          <i class="fas fa-angle-double-left"></i>
        </button>
        <button
          @click="currentPage--"
          :disabled="currentPage === 1"
          class="page-button"
          title="Página anterior"
        >
          <i class="fas fa-angle-left"></i>
        </button>

        <div class="page-numbers">
          <button
            v-for="page in visiblePages"
            :key="page"
            @click="currentPage = page"
            :class="['page-number', { active: currentPage === page }]"
          >
            {{ page }}
          </button>
        </div>

        <button
          @click="currentPage++"
          :disabled="currentPage === totalPages"
          class="page-button"
          title="Página siguiente"
        >
          <i class="fas fa-angle-right"></i>
        </button>
        <button
          @click="currentPage = totalPages"
          :disabled="currentPage === totalPages"
          class="page-button"
          title="Última página"
        >
          <i class="fas fa-angle-double-right"></i>
        </button>

        <div class="page-info">
          Página {{ currentPage }} de {{ totalPages }}
        </div>
      </div>
    </div>

    <!-- Import Modal -->
    <Modal v-model="showImportModal" title="Importar Noticias de Escuela" size="lg">
      <SchoolNewsImport :schoolNews="schoolNews" @close="handleImportClose" />
    </Modal>

    <!-- Export Modal -->
    <Modal v-model="showExportModal" title="Exportar Noticias de Escuela" size="md">
      <SchoolNewsExport :schoolNews="schoolNews" @close="showExportModal = false" />
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useSchoolNews, type SchoolNewsItem } from '@/composables/useSchoolNews';
import Modal from '@/components/ui/Modal.vue';
import SchoolNewsImport from '@/components/admin/SchoolNewsImport.vue';
import SchoolNewsExport from '@/components/admin/SchoolNewsExport.vue';
import { QuillEditor } from '@vueup/vue-quill';

const emit = defineEmits<{
  openEditSchoolNewsModal: [newsItem: SchoolNewsItem]
}>();

const schoolNewsComposable = useSchoolNews();
const {
  createSchoolNews,
  deleteSchoolNews: deleteSchoolNewsItem,
  fetchSchoolNews,
} = schoolNewsComposable;
const schoolNews = schoolNewsComposable.schoolNews;
const schoolNewsLoading = schoolNewsComposable.loading;

const showCreateForm = ref(false);
const creatingSchoolNews = ref(false);
const deletingSchoolNews = ref<string | null>(null);
const searchQuery = ref('');
const showImportModal = ref(false);
const showExportModal = ref(false);
const itemsPerPage = ref(10);
const currentPage = ref(1);

const newSchoolNews = ref({
  title: '',
  content: '',
  createdAt: '',
});

const schoolNewsCreateEditorRef = ref<any>(null);

// Quill options (simplified for this component)
const quillOptions = {
  theme: 'snow',
  placeholder: 'Escribe el contenido de la noticia aquí...',
  modules: {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'font': [] }],
      [{ 'align': [] }],
      ['image'],
      ['link'],
      ['clean']
    ]
  }
};

const filteredSchoolNews = computed(() => {
  if (!searchQuery.value) return schoolNews.value;

  const query = searchQuery.value.toLowerCase();
  return schoolNews.value.filter(item =>
    item.title.toLowerCase().includes(query) ||
    item.content.toLowerCase().includes(query)
  );
});

// Pagination
const totalPages = computed(() => {
  return Math.ceil(filteredSchoolNews.value.length / itemsPerPage.value);
});

const paginatedSchoolNews = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredSchoolNews.value.slice(start, end);
});

const visiblePages = computed(() => {
  const pages: number[] = [];
  const maxVisible = 5;
  const total = totalPages.value;
  const current = currentPage.value;

  if (total <= maxVisible) {
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    let start = Math.max(1, current - Math.floor(maxVisible / 2));
    let end = Math.min(total, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
  }

  return pages;
});

// Reset to page 1 when search query or items per page changes
watch([searchQuery, itemsPerPage], () => {
  currentPage.value = 1;
});

const stripHtml = (html: string): string => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const truncateContent = (content: string, maxLength: number): string => {
  if (content.length <= maxLength) return content;
  return content.substring(0, maxLength).trim() + '...';
};

const handleCreateSchoolNews = async () => {
  if (!newSchoolNews.value.title.trim() || !newSchoolNews.value.content.trim()) return;

  creatingSchoolNews.value = true;
  const createdAt = newSchoolNews.value.createdAt ? new Date(newSchoolNews.value.createdAt) : undefined;
  const created = await createSchoolNews(newSchoolNews.value.title, newSchoolNews.value.content, createdAt);

  if (created) {
    newSchoolNews.value = { title: '', content: '', createdAt: '' };
    showCreateForm.value = false;
  }

  creatingSchoolNews.value = false;
};

const openEditSchoolNewsModal = (newsItem: SchoolNewsItem) => {
  emit('openEditSchoolNewsModal', newsItem);
};

const handleDeleteSchoolNews = async (newsItem: SchoolNewsItem) => {
  const ok = confirm(`¿Eliminar la noticia de la escuela "${newsItem.title}"? Esta acción no se puede deshacer.`);
  if (!ok) return;

  deletingSchoolNews.value = newsItem.id;
  await deleteSchoolNewsItem(newsItem.id);
  deletingSchoolNews.value = null;
};

const handleImportClose = async () => {
  showImportModal.value = false;
  await fetchSchoolNews();
};

</script>

<style scoped>
.school-news-management {
  width: 100%;
}

/* Action Bar */
.action-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-6);
  padding: var(--spacing-4);
  background: white;
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-md);
  gap: var(--spacing-4);
}

.action-bar-left {
  display: flex;
  gap: var(--spacing-3);
  flex-wrap: wrap;
}

.action-bar-right {
  display: flex;
  align-items: center;
}

.btn-primary,
.btn-secondary {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  padding: var(--spacing-3) var(--spacing-4);
  border: none;
  border-radius: var(--border-radius-lg);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: var(--transition-all);
  font-size: var(--font-size-sm);
}

.btn-primary {
  background: var(--color-primary-600);
  color: white;
}

.btn-primary:hover {
  background: var(--color-primary-700);
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

.btn-secondary {
  background: white;
  color: var(--color-neutral-700);
  border: 1px solid var(--color-neutral-300);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--color-neutral-50);
  border-color: var(--color-neutral-400);
  transform: translateY(-1px);
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.news-count {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  padding: var(--spacing-2) var(--spacing-4);
  background: var(--color-primary-50);
  border: 1px solid var(--color-primary-200);
  border-radius: var(--border-radius-full);
  color: var(--color-primary-700);
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-sm);
  white-space: nowrap;
}

.create-form-section {
  background: white;
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-lg);
  margin-bottom: var(--spacing-6);
  overflow: hidden;
}

.news-form {
  padding: var(--spacing-6);
}

/* Search and Filter Section */
.search-filter-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-4);
  margin-bottom: var(--spacing-6);
  flex-wrap: wrap;
}

.search-input-wrapper {
  position: relative;
  flex: 1;
  min-width: 250px;
  max-width: 400px;
}

.results-per-page {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  font-size: var(--font-size-sm);
  color: var(--color-neutral-700);
  font-weight: var(--font-weight-medium);
}

.page-size-select {
  padding: var(--spacing-2) var(--spacing-3);
  border: 1px solid var(--color-neutral-300);
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-sm);
  background: white;
  cursor: pointer;
  transition: var(--transition-all);
}

.page-size-select:focus {
  outline: none;
  border-color: var(--color-primary-500);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-input {
  width: 100%;
  padding: var(--spacing-3) var(--spacing-4) var(--spacing-3) var(--spacing-10);
  border: 1px solid var(--color-neutral-300);
  border-radius: var(--border-radius-lg);
  font-size: var(--font-size-base);
  transition: var(--transition-all);
  background: white;
}

.search-input:focus {
  outline: none;
  border-color: var(--color-primary-500);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-icon {
  position: absolute;
  left: var(--spacing-3);
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-neutral-400);
  font-size: var(--font-size-lg);
}

.clear-search {
  position: absolute;
  right: var(--spacing-3);
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--color-neutral-400);
  cursor: pointer;
  padding: var(--spacing-1);
  border-radius: var(--border-radius-sm);
  transition: var(--transition-all);
}

.clear-search:hover {
  background: var(--color-neutral-100);
  color: var(--color-neutral-600);
}

.clear-search-button {
  margin-top: var(--spacing-4);
  padding: var(--spacing-2) var(--spacing-4);
  background: var(--color-neutral-100);
  color: var(--color-neutral-700);
  border: 1px solid var(--color-neutral-300);
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: var(--transition-all);
}

.clear-search-button:hover {
  background: var(--color-neutral-200);
  border-color: var(--color-neutral-400);
}

/* Form styles */
.form-group {
  margin-bottom: var(--spacing-4);
}

.form-label {
  display: block;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-neutral-700);
  margin-bottom: var(--spacing-2);
}

.form-input,
.form-textarea {
  width: 100%;
  padding: var(--spacing-3) var(--spacing-4);
  border: 1px solid var(--color-neutral-300);
  border-radius: var(--border-radius-lg);
  font-size: var(--font-size-base);
  transition: var(--transition-all);
  background: white;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: var(--color-primary-500);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input:disabled,
.form-textarea:disabled {
  background: var(--color-neutral-100);
  cursor: not-allowed;
}

.form-textarea {
  resize: vertical;
  min-height: 120px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-3);
  margin-top: var(--spacing-6);
}

.create-button {
  background: var(--color-primary-600);
  color: white;
  border: none;
  padding: var(--spacing-3) var(--spacing-6);
  border-radius: var(--border-radius-lg);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: var(--transition-all);
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
}

.create-button:hover:not(:disabled) {
  background: var(--color-primary-700);
  transform: translateY(-1px);
}

.create-button:disabled {
  background: var(--color-neutral-400);
  cursor: not-allowed;
  transform: none;
}

.form-help {
  display: block;
  font-size: var(--font-size-sm);
  color: var(--color-neutral-500);
  margin-top: var(--spacing-1);
}

/* Loading and Empty states */
.loading-state,
.empty-state {
  text-align: center;
  padding: var(--spacing-12) var(--spacing-6);
  background: white;
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-lg);
}

.loading-spinner {
  font-size: 2rem;
  color: var(--color-primary-600);
  margin-bottom: var(--spacing-4);
}

.empty-icon {
  font-size: 4rem;
  color: var(--color-neutral-400);
  margin-bottom: var(--spacing-4);
}

.empty-state h3 {
  font-size: var(--font-size-xl);
  color: var(--color-neutral-600);
  margin: 0 0 var(--spacing-2) 0;
}

.empty-state p {
  color: var(--color-neutral-500);
  margin: 0;
}

/* News Table */
.news-table-wrapper {
  background: white;
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
}

.news-table {
  width: 100%;
  border-collapse: collapse;
}

.news-table thead {
  background: linear-gradient(135deg, #f8fafc, #f1f5f9);
  border-bottom: 2px solid var(--color-neutral-200);
}

.news-table th {
  padding: var(--spacing-4) var(--spacing-4);
  text-align: left;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-neutral-700);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.news-table tbody tr {
  border-bottom: 1px solid var(--color-neutral-100);
  transition: var(--transition-all);
}

.news-table tbody tr:hover {
  background: var(--color-primary-50);
}

.news-table tbody tr:last-child {
  border-bottom: none;
}

.news-table td {
  padding: var(--spacing-4);
  vertical-align: middle;
}

.col-title {
  width: 50%;
}

.col-date {
  width: 20%;
}

.col-author {
  width: 15%;
}

.col-actions {
  width: 15%;
  text-align: right;
}

.news-title-cell {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-1);
}

.news-title {
  font-weight: var(--font-weight-semibold);
  color: var(--color-neutral-900);
  font-size: var(--font-size-base);
  display: block;
}

.news-preview {
  font-size: var(--font-size-sm);
  color: var(--color-neutral-500);
  line-height: 1.4;
  display: block;
}

.news-table .col-date time {
  font-size: var(--font-size-sm);
  color: var(--color-neutral-600);
}

.author-cell {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  font-size: var(--font-size-sm);
  color: var(--color-neutral-600);
}

.action-buttons {
  display: flex;
  gap: var(--spacing-2);
  justify-content: flex-end;
}

.edit-button {
  background: var(--color-primary-50);
  color: var(--color-primary-600);
  border: 1px solid var(--color-primary-200);
  padding: var(--spacing-2);
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: var(--transition-all);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
}

.edit-button:hover:not(:disabled) {
  background: var(--color-primary-100);
  border-color: var(--color-primary-300);
}

.delete-button {
  background: var(--color-error-50);
  color: var(--color-error-600);
  border: 1px solid var(--color-error-200);
  padding: var(--spacing-2);
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: var(--transition-all);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
}

.delete-button:hover:not(:disabled) {
  background: var(--color-error-100);
  border-color: var(--color-error-300);
}

.delete-button:disabled {
  background: var(--color-neutral-100);
  border-color: var(--color-neutral-200);
  color: var(--color-neutral-400);
  cursor: not-allowed;
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-2);
  padding: var(--spacing-6);
  border-top: 1px solid var(--color-neutral-200);
  background: linear-gradient(to bottom, white, #f8fafc);
}

.page-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  background: white;
  border: 1px solid var(--color-neutral-300);
  border-radius: var(--border-radius-md);
  color: var(--color-neutral-600);
  cursor: pointer;
  transition: var(--transition-all);
  font-size: var(--font-size-sm);
}

.page-button:hover:not(:disabled) {
  background: var(--color-primary-50);
  border-color: var(--color-primary-300);
  color: var(--color-primary-600);
}

.page-button:disabled {
  background: var(--color-neutral-50);
  border-color: var(--color-neutral-200);
  color: var(--color-neutral-300);
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: var(--spacing-2);
}

.page-number {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0 var(--spacing-2);
  background: white;
  border: 1px solid var(--color-neutral-300);
  border-radius: var(--border-radius-md);
  color: var(--color-neutral-600);
  cursor: pointer;
  transition: var(--transition-all);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}

.page-number:hover {
  background: var(--color-primary-50);
  border-color: var(--color-primary-300);
  color: var(--color-primary-600);
}

.page-number.active {
  background: var(--color-primary-600);
  border-color: var(--color-primary-600);
  color: white;
}

.page-info {
  margin-left: var(--spacing-3);
  font-size: var(--font-size-sm);
  color: var(--color-neutral-600);
  font-weight: var(--font-weight-medium);
}

/* Quill Editor Styles */
.quill-editor {
  border: 1px solid var(--color-neutral-300);
  border-radius: var(--border-radius-lg);
  background: white;
  width: 100%;
  display: block;
}

.quill-editor:focus-within {
  border-color: var(--color-primary-500);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.quill-editor .ql-toolbar {
  border-top-left-radius: var(--border-radius-lg);
  border-top-right-radius: var(--border-radius-lg);
  border-bottom: 1px solid var(--color-neutral-300);
  width: 100% !important;
  box-sizing: border-box;
}

.quill-editor .ql-container {
  border-bottom-left-radius: var(--border-radius-lg);
  border-bottom-right-radius: var(--border-radius-lg);
  min-height: 300px;
  width: 100% !important;
  box-sizing: border-box;
}

.quill-editor .ql-editor {
  min-height: 300px;
  font-size: var(--font-size-base);
  line-height: 1.6;
  max-width: none;
  width: 100% !important;
  box-sizing: border-box;
}

.quill-editor .ql-editor.ql-blank::before {
  color: var(--color-neutral-500);
  font-style: normal;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .col-date,
  .col-author {
    display: none;
  }

  .col-title {
    width: 70%;
  }

  .col-actions {
    width: 30%;
  }
}

@media (max-width: 768px) {
  .action-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .action-bar-left,
  .action-bar-right {
    width: 100%;
    justify-content: space-between;
  }

  .news-form {
    padding: var(--spacing-4);
  }

  .news-table th,
  .news-table td {
    padding: var(--spacing-3);
  }

  .news-preview {
    display: none;
  }

  .pagination {
    flex-wrap: wrap;
    gap: var(--spacing-3);
  }

  .page-info {
    width: 100%;
    text-align: center;
    margin: 0;
    order: -1;
  }
}

@media (max-width: 640px) {
  .search-filter-section {
    flex-direction: column;
    align-items: stretch;
  }

  .search-input-wrapper {
    max-width: 100%;
  }

  .results-per-page {
    justify-content: space-between;
  }

  .form-actions {
    flex-direction: column;
  }

  .create-button {
    width: 100%;
    justify-content: center;
  }

  .action-bar-left {
    width: 100%;
    justify-content: stretch;
  }

  .btn-primary,
  .btn-secondary {
    flex: 1;
    justify-content: center;
  }

  .page-numbers {
    flex-wrap: wrap;
  }
}
</style>


